import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import TextInput from '../../components/ui/TextInput';
import PersonCell from '../../components/ui/PersonCell';
import api from '../../services/api';
import { createShiftSwap, getTargetShifts } from '../../services/shiftSwapService';

const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// ─── Styled Components (WorkHourPickerModal.js overlay/dialog deseni) ────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  animation: ${fadeIn} 0.15s ease;
`;

const Dialog = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 480px;
  max-width: 92vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.18);
  animation: ${slideUp} 0.18s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TitleText = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 700;
  font-family: 'Nunito Sans', sans-serif;
`;

const Subtitle = styled.span`
  font-size: 12px;
  color: #7A8694;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  &:hover { background: #F5F7FA; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #E8ECF0;
`;

// ctx: { shiftId, date, workHourName } — set by MySchedulePage when a
// swap-eligible cell is clicked (see MySchedulePage.js's setSwapCtx).
export default function SwapRequestModal({ isOpen, ctx, onClose, onCreated }) {
  const { t } = useTranslation();

  const [type, setType] = useState('trade');
  const [targetUserId, setTargetUserId] = useState(null);
  const [targetShiftId, setTargetShiftId] = useState(null);
  const [note, setNote] = useState('');
  const [shiftOptions, setShiftOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Reset the form on every open — otherwise the previous swap request's
  // leftovers (B, Y, note) would bleed into the next cell's context.
  useEffect(() => {
    if (!isOpen) return;
    setType('trade');
    setTargetUserId(null);
    setTargetShiftId(null);
    setNote('');
    setShiftOptions([]);
  }, [isOpen]);

  // B seçilince (yalnız trade tipinde) hedef çalışanın yayınlanmış gelecek
  // vardiyalarını yükle — Y seçimi için. B veya tip değişince önceki Y
  // seçimi artık geçersiz olabileceğinden sıfırlanır. cancelled guard'ı
  // ShiftSchedulesPage.js'in workHours efekti ile aynı desen.
  useEffect(() => {
    setTargetShiftId(null);
    if (!isOpen || type !== 'trade' || !targetUserId) {
      setShiftOptions([]);
      return undefined;
    }
    let cancelled = false;
    const today = new Date();
    const until = new Date();
    until.setDate(until.getDate() + 60);
    getTargetShifts({
      targetUserId,
      from: formatDateISO(today),
      to: formatDateISO(until),
    })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data || [];
        setShiftOptions(list.map((s) => ({ id: s.id, name: `${s.date} ${s.workHourName || ''}`.trim() })));
      })
      .catch(() => { if (!cancelled) setShiftOptions([]); });
    return () => { cancelled = true; };
  }, [isOpen, type, targetUserId]);

  const typeOptions = useMemo(() => ([
    { id: 'trade', name: t('shiftSwap.type.trade') },
    { id: 'cover', name: t('shiftSwap.type.cover') },
  ]), [t]);

  // AsyncGenericSelect employee search — mirrors ShiftSchedulesPage.js's
  // searchEmployees exactly (same params, same result-shape tolerance).
  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const handleRequestClose = useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [saving, onClose]);

  // No try/catch here on purpose — the Axios interceptor already toasts the
  // error and rejects the promise for mutations; a rejection here simply
  // means the .then() below never runs, so the modal stays open. `finally`
  // only clears the local saving/disabled state and does not touch the
  // error itself.
  const handleSave = useCallback(() => {
    if (!ctx || saving) return;
    setSaving(true);
    createShiftSwap({
      type,
      requesterShiftId: ctx.shiftId,
      targetUserId,
      targetShiftId: type === 'trade' ? targetShiftId : undefined,
      requesterNote: note || undefined,
    })
      .then(() => {
        onCreated?.();
        onClose?.();
      })
      .finally(() => setSaving(false));
  }, [ctx, saving, type, targetUserId, targetShiftId, note, onCreated, onClose]);

  if (!isOpen) return null;

  const canSave = !!targetUserId && (type !== 'trade' || !!targetShiftId);

  return (
    <Overlay onClick={handleRequestClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <TitleText>{t('shiftSwap.request.title')}</TitleText>
            {ctx && <Subtitle>{ctx.date} • {ctx.workHourName || ''}</Subtitle>}
          </Title>
          <CloseBtn onClick={handleRequestClose} aria-label={t('common.close')}>
            <X size={18} />
          </CloseBtn>
        </Header>

        <Body>
          <Field>
            <GenericSelect
              label={t('shiftSwap.field.type')}
              options={typeOptions}
              value={type}
              onChange={(val) => { if (val) setType(val); }}
            />
          </Field>

          <Field>
            <AsyncGenericSelect
              label={t('shiftSwap.field.employee')}
              value={targetUserId}
              onChange={setTargetUserId}
              onSearch={searchEmployees}
              renderOption={(opt) => (
                <PersonCell
                  name={opt.name || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()}
                  profilePicture={opt.profilePicture}
                />
              )}
              required
            />
          </Field>

          {type === 'trade' && (
            <Field>
              <GenericSelect
                label={t('shiftSwap.field.targetShift')}
                options={shiftOptions}
                value={targetShiftId}
                onChange={setTargetShiftId}
                required
              />
            </Field>
          )}

          <Field>
            <TextInput
              label={t('shiftSwap.field.note')}
              type="textarea"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </Body>

        <Footer>
          <Button variant="ghost" onClick={handleRequestClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !canSave}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Footer>
      </Dialog>
    </Overlay>
  );
}
